import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Link from 'next/link';
import { useTranslation } from 'next-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';

import {
  selectFavoritesIdsAuthenticated,
  selectFavoritesIdsUnauthenticated,
} from '@selectors/favorites';
import { selectIsUserLogged } from '@selectors/auth';
import {
  unfavoriteUnauthenticated,
  favoriteUnauthenticated,
} from '@actions/favorites';
import { unfavorite, favorite } from '@thunks/favorites';

import { getRouteUrl } from '@utils/urls';

import Pill from '@common/components/Pill';
import Heart from '@common/components/Heart';
import OfferPill from '@common/components/OfferPill';
import { IconButton } from '@common/components/IconButton';
import { VARIANTS } from '@common/constants/pills';
import { PRODUCTS_CATEGORY } from '@common/routes';

import { PillsFavoritesWrapper, PillsWrapper } from './Pills.styled';

const Pills = ({ categories, productColorId, offerType }) => {
  const {
    i18n: { language },
  } = useTranslation();
  const dispatch = useDispatch();
  const [isFavoriteToggle, setIsFavoriteToggle] = useState(false);
  const { events: routerEvents } = useRouter();

  const isUserLogged = useSelector(selectIsUserLogged);

  const favoriteIdsAuthenticated = useSelector(selectFavoritesIdsAuthenticated);
  const isFavoritedInStore = favoriteIdsAuthenticated.includes(productColorId);
  const favoriteIdsUnuthenticated = useSelector(
    selectFavoritesIdsUnauthenticated,
  );
  const isFavoritedInLocalStorage =
    favoriteIdsUnuthenticated.includes(productColorId);
  const isFavorited = isUserLogged
    ? isFavoritedInStore
    : isFavoritedInLocalStorage;

  const toggleFavorite = useCallback(() => {
    if (isUserLogged) {
      if (isFavoritedInStore) dispatch(unfavorite(productColorId));
      else dispatch(favorite(productColorId));
    } else if (isFavoritedInLocalStorage)
      dispatch(unfavoriteUnauthenticated(productColorId));
    else dispatch(favoriteUnauthenticated(productColorId));

    setIsFavoriteToggle(true);
  }, [
    dispatch,
    productColorId,
    isUserLogged,
    isFavoritedInLocalStorage,
    isFavoritedInStore,
  ]);

  useEffect(() => {
    setIsFavoriteToggle(false);
    const onRouteChangeComplete = () => {
      setIsFavoriteToggle(false);
    };

    routerEvents.on('routeChangeComplete', onRouteChangeComplete);

    return () => {
      routerEvents.off('routeChangeComplete', onRouteChangeComplete);
    };
  }, [routerEvents]);

  return (
    <PillsFavoritesWrapper>
      <PillsWrapper>
        {categories?.map(({ id, slug, title }) => (
          <Link
            key={id}
            legacyBehavior
            href={{
              pathname: PRODUCTS_CATEGORY,
              query: { id, slug },
            }}
            as={getRouteUrl(language, PRODUCTS_CATEGORY, { id, slug })}
            passHref
          >
            <Pill as="a" variant={VARIANTS.LIGHT}>
              {title}
            </Pill>
          </Link>
        ))}
        <OfferPill offerType={offerType} />
      </PillsWrapper>
      <IconButton
        data-testid="favorite-button"
        onClick={toggleFavorite}
        icon={
          <Heart
            data-testid={isFavorited ? 'favorite-icon-on' : 'favorite-icon-off'}
            $isFavorited={isFavorited}
            $isFavoriteToggle={isFavoriteToggle}
          />
        }
      />
    </PillsFavoritesWrapper>
  );
};

Pills.propTypes = {
  categories: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      slug: PropTypes.string,
      title: PropTypes.string,
    }),
  ),
  productColorId: PropTypes.string.isRequired,
  offerType: PropTypes.string,
};

Pills.defaultProps = {
  categories: [],
  offerType: null,
};

export default Pills;
